import { useReexecuteQueryWhenInputsChange, useSecureGraphqlQuery } from '@psyomics/ui-common';
import { useMemo } from 'react';

import {
  GetReferralsDocument,
  GetReferralsFilters,
  GetReferralsQuery,
  GetReferralsQueryVariables,
  ReferralSortField,
  SortDirection,
} from './generated/referrals';
import { AllFilters } from './types';
import { useQueryFilters } from './use-filters';
import { isValidSort } from './use-paging-and-sorting';

const DEFAULT_SORT_FIELD = ReferralSortField.CreatedAt;

type UseQueryInput = {
  globalSearch: string | null;
  pageSize: number;
  pageIndex: number;
  sortField: ReferralSortField;
  sortAscending: boolean;
  queryFilters: GetReferralsFilters;
};
const useQueryInput = ({
  globalSearch: search,
  pageIndex,
  pageSize,
  sortField,
  sortAscending,
  queryFilters,
}: UseQueryInput) =>
  useMemo(
    () => ({
      query: GetReferralsDocument,
      pause: true,
      variables: {
        input: {
          filters: queryFilters,
          pagedQuery: {
            pageSize,
            offset: pageIndex * pageSize,
          },
          search,
          sort: {
            field: sortField,
            direction: sortAscending ? SortDirection.Asc : SortDirection.Desc,
          },
        },
      },
    }),
    [search, pageIndex, pageSize, sortField, sortAscending, queryFilters]
  );

type UseReferralsQueryInput = {
  pageSize: number;
  pageIndex: number;
  sortBy: string;
  sortAscending: boolean;
  allFilters: AllFilters;
  showCompletedOnly?: boolean;
};
const useReferralsQuery = ({
  pageSize,
  pageIndex,
  sortBy,
  sortAscending,
  allFilters,
  showCompletedOnly,
}: UseReferralsQueryInput) => {
  const sortField = isValidSort(sortBy) ? (sortBy as ReferralSortField) : DEFAULT_SORT_FIELD;
  const { filters } = allFilters;

  const queryFilters = useQueryFilters(filters, showCompletedOnly);
  const queryInput = useQueryInput({
    globalSearch: allFilters.globalSearch.value,
    pageIndex,
    pageSize,
    queryFilters,
    sortAscending,
    sortField,
  });

  const [{ data, fetching }, reexecuteQuery] = useSecureGraphqlQuery<GetReferralsQuery, GetReferralsQueryVariables>(
    queryInput
  );

  useReexecuteQueryWhenInputsChange(reexecuteQuery, queryInput);

  const result = data?.getReferrals;

  return { result, fetching };
};

export { useReferralsQuery };
